import { capitalize, isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Divider } from 'src/@vymo/ui/atoms';
// import { useNavigate } from 'react-router-dom';
import Button from 'src/@vymo/ui/atoms/button';
import Text from 'src/@vymo/ui/atoms/text';
import {
  Banner,
  Body,
  Modal,
  Header as ModalHeader,
} from 'src/@vymo/ui/blocks';
import Accordion from 'src/@vymo/ui/blocks/accordion';
import { Keys, locale } from 'src/i18n';
import { getPendingActivities } from 'src/models/lmsConfig/selectors';
import { REVYRE_FORM, REVYRE_NOTIFICATION } from 'src/modules/constants';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { renderDialog } from 'src/workspace/slice';
import { REVYRE } from '../constants';
import { getWorkflowCode } from '../form/queries';
import { getFeatureConfigByType } from '../selectors';
import { initiatePhoneCall } from '../thunk';
import { ClickToCallPayload } from '../types';
import {
  buildProfileActionOptions,
  getQuickActionButtonHint,
  getReferredActions,
  shouldShowDetailsPageQuickAction,
} from './queries';
import {
  getEntityDetailsInputsMap,
  getHeaderActionButtons,
  getQuickActions,
  getReferredEntityDetails,
} from './selectors';
import styles from './index.module.scss';

function Header({ domainType, entityId, workflowType }) {
  const dispatch = useAppDispatch();
  const [showModal, setShowModal] = useState<string>('');
  const actionButtons = useAppSelector((state) =>
    getHeaderActionButtons(state, entityId, domainType, workflowType),
  );
  const referredEntityDetails = useAppSelector((state) =>
    getReferredEntityDetails(state, entityId),
  );
  const inputsMap = useAppSelector((state) =>
    getEntityDetailsInputsMap(state, entityId),
  );
  const isClickToCallEnabled =
    useAppSelector((state) =>
      getFeatureConfigByType(state, inputsMap?.workflowType || workflowType),
    )?.atcConfig?.enableClickToCall || false;
  const inputsMapWorkflowId = getWorkflowCode(inputsMap?.workflowId);
  const quickActions = useAppSelector((state) =>
    getQuickActions(
      state,
      domainType === 'workflow' ? entityId : inputsMapWorkflowId,
    ),
  );
  const [showCallAlert, setShowCallAlert] = useState(false);

  const referredActions = getReferredActions(
    domainType,
    referredEntityDetails,
    workflowType,
  );
  const handleButtonOnClick = (type, isBlockingScreen?) => {
    if (isBlockingScreen) {
      dispatch(
        renderDialog({
          id: REVYRE_NOTIFICATION,
          props: {
            type,
            domainType,
            isBlockingScreen,
            workflowType,
            activityType:
              domainType === 'activity' ? inputsMap?.activityType : undefined,
          },
        }),
      );
    } else {
      dispatch(
        renderDialog({
          id: REVYRE_FORM,
          props: {
            type,
            domainType,
            activityType:
              domainType === 'activity' ? inputsMap?.activityType : undefined,
          },
        }),
      );
    }
  };

  const pendingActivities = useAppSelector((state) =>
    getPendingActivities(state),
  );
  const pendingActivitiesCodes = pendingActivities.map(
    (item) => item?.mutatedEntity?.id,
  );

  useEffect(() => {
    if (pendingActivitiesCodes.includes(entityId) && !isEmpty(inputsMap)) {
      handleButtonOnClick('update', true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    entityId,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(pendingActivitiesCodes),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(inputsMap),
  ]);

  const handleReferredActionClick = (
    buttonScenario,
    type,
    buttonDomainType,
    buttonWorkflowType,
    buttonMetadata,
  ) => {
    const isCustomRefferedEntity =
      REVYRE.COLLECT_IQ.DETAILS_PAGE_CUSTOM_REFFERED_ENTITIES.includes(
        buttonDomainType,
      );

    dispatch(
      renderDialog({
        id: REVYRE_FORM,
        props: {
          scenario: buttonScenario,
          type,
          domainType: buttonDomainType,
          workflowType: buttonWorkflowType,
          ...(isCustomRefferedEntity && {
            buttonMetadata: {
              ...buttonMetadata,
              parentAssociateWorkflowType: workflowType,
            },
          }),
        },
      }),
    );
  };

  const handleQuickActionItemClick = (type: string, item) => {
    switch (type) {
      case 'navigate':
        if (item.url) {
          window.open(item.url, '_blank');
        }
        break;

      case 'email':
        if (item.value) {
          window.open(`mailto:${item.value}`, '_blank');
        }
        break;

      case 'call':
        if (item.value) {
          window.open(`tel:${item.value}`, '_blank');
        }
        break;

      case 'sms':
        if (item.value) {
          window.open(`sms:${item.value}`, '_blank');
        }
        break;

      default:
    }
  };

  const callSelectedEntity = async (selectedOption) => {
    const { id, profileId } = selectedOption;

    const basePayload: ClickToCallPayload = {
      profileId,
      phoneId: id,
    };

    const payload =
      domainType === 'activity'
        ? {
            workflowId: inputsMapWorkflowId,
            workflowType: domainType,
            activityId: entityId,
          }
        : {
            workflowType: workflowType ?? domainType,
            workflowId: entityId,
          };

    const clickToCallPayload = {
      ...basePayload,
      ...payload,
    };

    const success = await dispatch(initiatePhoneCall(clickToCallPayload));

    if (success) {
      setShowCallAlert(true);
    }
  };

  const handleQuickAction = (actionType, option) => {
    if (isClickToCallEnabled && actionType === 'call') {
      callSelectedEntity(option);
      setShowModal('');
    } else {
      handleQuickActionItemClick(actionType, option);
    }
  };

  return (
    <div
      data-test-id="entity-details-header"
      className={styles.details__header}
    >
      {showCallAlert && (
        <Banner
          message={locale(Keys.CALL_INITIATED_MESSAGE)}
          variant="info"
          position="topRight"
          duration={5000}
          closeable
        />
      )}

      {pendingActivitiesCodes.length > 0 && (
        <Banner
          message={`${locale(Keys.YOU_HAVE)} ${
            pendingActivitiesCodes.length
          } ${locale(Keys.PENDING_ACTIVITIES_INFO)}`}
          variant="warning"
          position="topRight"
          duration={5000}
          closeable
        />
      )}
      {shouldShowDetailsPageQuickAction({
        domainType,
        inputsMap,
        workflowType,
      }) && (
        <div className={styles.details__header__events}>
          <div className={styles.details__header__events__section}>
            {actionButtons?.map((button) => (
              <Button
                data-test-id={`${button?.hint}-action-button`}
                className={styles.details__header__events__button}
                variant="subtle"
                disabled={button?.disabled}
                onClick={() => handleButtonOnClick(button.type)}
              >
                {button?.hint}
              </Button>
            ))}
            {(quickActions || [])?.map((action) => {
              const profileActions = buildProfileActionOptions(action);

              return (
                <>
                  <Button
                    data-test-id={`${action?.type}-quick-action-button`}
                    className={styles.details__header__events__button}
                    variant="subtle"
                    onClick={() => {
                      setShowModal(action.type);
                      setShowCallAlert(false);
                    }}
                  >
                    {getQuickActionButtonHint(action?.type)}
                  </Button>
                  {showModal === action.type && (
                    <Modal
                      open={Boolean(showModal.length)}
                      showCloseButton
                      onClose={() => setShowModal('')}
                      classNames={styles.details__quickActionModal}
                    >
                      <ModalHeader
                        className={styles.details__quickActionModal__header}
                      >
                        <Text type="h4">{capitalize(action?.type)}</Text>
                      </ModalHeader>
                      <Body className={styles.details__quickActionModal__body}>
                        {profileActions.map((profileOption) => (
                          <>
                            <Accordion
                              title={
                                <Text type="h5">{profileOption.name}</Text>
                              }
                              type="subtle"
                              description={profileOption.subLabel}
                              open
                            >
                              {profileOption.options.map((option) => (
                                // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                                <div
                                  className={
                                    styles.details__quickActionModal__body__item
                                  }
                                  onClick={() =>
                                    handleQuickAction(action?.type, option)
                                  }
                                >
                                  <div
                                    className={`${styles.details__quickActionModal__body__item__content}`}
                                  >
                                    <Text type="subText" semiBold>
                                      {option.name}
                                    </Text>
                                    <Text type="label">{option.subLabel}</Text>
                                  </div>

                                  <div
                                    className={`${styles.details__quickActionModal__body__item__icon}`}
                                  >
                                    {option.icon}
                                  </div>
                                </div>
                              ))}
                            </Accordion>
                            <Divider />
                          </>
                        ))}
                      </Body>
                    </Modal>
                  )}
                </>
              );
            })}
          </div>
          <div className={styles.details__header__events__section}>
            {referredActions?.map((action) => (
              <Button
                data-test-id={`${
                  action?.code || action?.workflowType
                }-referred-action-button`}
                className={styles.details__header__events__button}
                variant="subtle"
                disabled={action.disabled}
                onClick={() =>
                  handleReferredActionClick(
                    action.scenario,
                    action.type,
                    action.domainType,
                    action.workflowType,
                    action.buttonMetadata,
                  )
                }
              >
                {action?.hint}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Header;
